import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import OrganizationSetting from "@/services/organization/organization-setting";
import { DialerRotationStrategy } from "@/utils/enums";

export interface DialerRotationSettingParams {
  organizationId: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useDialerRotationSetting(
  params: Ref<DialerRotationSettingParams | null> | DialerRotationSettingParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["dialerRotationSetting", "disabled"];
    return ["dialerRotationSetting", p.organizationId];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const result = await OrganizationSetting.getDialerRotationSetting({
        organizationId: p.organizationId,
      });

      const data = result.data?.status && result.data?.data ? result.data.data : result.data;

      return {
        dialerRotationStrategy: data?.dialerRotationStrategy || DialerRotationStrategy.default,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
